  import React, { useState, useEffect, useMemo } from "react";
  import { Helmet } from "react-helmet";
  import { Link } from "react-router-dom";
import Hero from "./components/Hero";
import Seo from "./components/seo";
import { apiUrlWithLang, currentLanguage, withLanguage } from "./utils/locale";
import { translateCardName } from "./utils/i18n";
  

  /* ==============================
    THEME (match other pages)
  ============================== */

  const WRAP = "px-[1vw]";
  const MAXW = "max-w-10xl mx-auto";
  const FONT = "font-[Cormorant_Garamond]";
  const TEXT = "text-white/90";
  const PANEL = "relative overflow-hidden rounded-none border-2 border-white/30 shadow-[0_1px_2px_0_rgba(0,0,0,0.8)] backdrop-blur-lg bg-[linear-gradient(135deg,rgba(0,137,166,0.76),rgba(1,76,117,0.72)_48%,rgba(2,22,48,0.86))]";

  // icon glow tokens
const ICON_GLOW_BASE =
  "w-24 h-24 object-contain select-none pointer-events-none transition duration-200 ease-out drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]";

const ICON_GLOW_HOV =
  "group-hover:scale-105 motion-reduce:group-hover:scale-100 " +
  "group-hover:brightness-125 group-hover:contrast-110 " +
  "group-hover:drop-shadow-[0_0_2px_rgba(255,255,255,0.2)]"; // crisp white halo

  function formatFriday(dt, locale) {
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(dt);
}
  /* ==============================
    SEO
  ============================== */
  const siteUrl = "https://tarotfate.com";
  const pageUrl = `${siteUrl}/`;
  const ogImage = `${siteUrl}/icons/og-cover.jpg`;
  const logoUrl = `${siteUrl}/icons/logo.webp`;

  const metaDescription =
    "Free emotion-based tarot readings, card meanings and practical spreads for love, work, study and personal clarity. Reflect gently, then choose your next step.";
  const keywords = `
  online tarot, emotion-based tarot, free tarot reading, tarot card meanings, single card, three card spread, five card spread,
  upright meanings, reversed meanings, love tarot, career tarot, study tarot, personal growth, mindful tarot, reflective tarot
  `.replace(/\s+/g, " ").trim();

  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoUrl, width: 512, height: 512 }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
        potentialAction: {
          "@type": "SearchAction",
          target: `${siteUrl}/search?query={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "TarotFate | Emotion-Based Online Tarot Readings",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@type": "ImageObject", url: ogImage, width: 1200, height: 630 },
        inLanguage: "en"
      }
    ]
  };

  // Image fallback helper (tries multiple sources)
function ImgFallback({ srcs, alt, className }) {
  const [i, setI] = React.useState(0);
  if (!srcs?.length) return null;
  return (
    <img
      src={srcs[i]}
      alt={alt}
      onError={() => setI((p) => (p + 1 < srcs.length ? p + 1 : p))}
      className={className}
      decoding="async"
      draggable="false"
    />
  );
}

// Quick Oracles just below Emotions
const QUICK_FEATURES = [
  {
    key: "yesno",
    title: "Yes / No Tarot",
    to: "/yes-no-tarot",
    // prefer your .web, then .webp, then .png
    srcs: ["/icons/yesno.web", "/icons/yesno.webp", "/icons/yesno.png"],
    cta: "Start",
    explain: "Ask one focused question and draw one card for Yes, No or Wait. Simple and direct.",
  },
  {
    key: "readmycard",
    title: "Read My Card(s)",
    to: "/readmycard",                    // <-- route for your Readymycard page
    srcs: ["/icons/readmycard.webp", "/icons/readmycard.png"],
    cta: "Open",
    explain: "Pick your own cards and get clear meanings for each, upright or reversed.",
  },
  {
    key: "tarotscope",
    title: "Tarotscope",
    to: "/tarotscope",
    srcs: ["/icons/tarotscope.web", "/icons/tartoscope.webp", "/icons/tarotscope.webp", "/icons/tarotscope.png"],
    cta: "Explore",
    explain: "Discover how tarot blends with astrology to reveal guidance aligned with your zodiac sign.",
  },
];


  /* ==============================
    API + helpers
  ============================== */
  const API_BASE =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
    "https://tarotfate-api.tarotfate-com.workers.dev";

  const emotions = [
    { label: "Uncertain-Love", description: "Unsure if heart is truly seen and returned." },
    { label: "Heart-broken", description: "Mourning love that's been lost." },
    { label: "Anxious", description: "Worried about what's coming." },
    { label: "Confused", description: "Torn between choices or paths." },
    { label: "Lonely", description: "Craving love, connection or understanding." },
    { label: "Hopeless", description: "Feeling like nothing will change." },
    { label: "Guilty", description: "Haunted by past decisions." },
    { label: "Betrayed", description: "Wounded by trust broken or promises lost." },
    { label: "Insecure", description: "Doubting your worth or place in someone's heart." },
    { label: "Overwhelmed", description: "Drowning in too many feelings or expectations." },
    { label: "Other", description: "Something else stirs your soul." }
  ];

  const spreads = [
    { value: 1, title: "The Whisper", icon: "Spread1.png", desc: "A single card to gently reflect your emotional state in this moment." },
    { value: 3, title: "The Thread", icon: "Spread3.png", desc: "A linear 3 cards spread that follows your journey: where you've been, where you are, where you're headed." },
    { value: 5, title: "The Mirror", icon: "Spread5.png", desc: "Five cards that dive deeper into the emotional terrain and guide your next move." }
  ];

  const HOME_COPY_ES = {
    title: "TarotFate | Lecturas de tarot en linea segun tu emocion",
    description:
      "Lecturas gratuitas de tarot segun tu emocion, significados de cartas y tiradas practicas para el amor, el trabajo, el estudio y la claridad personal.",
    keywords:
      "tarot en linea, lectura de tarot gratis, tarot por emociones, significados de cartas, tirada de una carta, tirada de tres cartas, amor, claridad emocional",
    heroLine1: "En esta quietud sagrada, el corazon habla con simbolos y silencio",
    heroLine2: "Tu emocion guia. Las cartas del tarot la reflejan.",
    feelingPrompt: "Selecciona una emocion abajo para explorar una reflexion de tarot personalizada.",
    publishedWeekly: "Publicado semanalmente:",
    loading: "Cargando...",
    fateFriday: "Viernes del destino",
    fateFridayTitleFallback: "Guia semanal: El poder de reflexionar",
    fateFridayFallback:
      "Esta semana invita a bajar el ritmo y mirar con honestidad lo que pide tu energia. La reflexion no es quedarse detenida; es reconocer que necesita mas cuidado, que puede soltar peso y que pequeno paso te devuelve claridad. Aparta un momento tranquilo, revisa lo que si esta funcionando y elige una accion sencilla para avanzar con mas calma.",
    fateFridayCta: "Leer la guia del viernes",
    fullMoonHighlight: "Guia de luna llena",
    fullMoon: "Luna llena",
    fullMoonFallback:
      "Esta luna llena abre un espacio para agradecer, cerrar ciclos suaves y recuperar claridad emocional. Mira lo que has sostenido, decide que asuntos necesitan una conversacion honesta y elige un gesto concreto que te ayude a descansar mejor en los proximos dias.",
    fullMoonCta: "Leer la guia de luna llena",
    selectSpread: "Elige una tirada de cartas",
    selectSpreadMessage:
      "Elige una tirada. Cada carta se interpreta en sintonia con tu emocion para ofrecerte una lectura mas personal.",
    otherRequired: "Para continuar con otra emocion, describe lo que estas sintiendo.",
    whisperPrompt:
      "Para una lectura mas profunda y personal, tambien puedes compartir lo que tu corazon quiere decir.",
    whisperRequiredPlaceholder: "Escribe tu emocion",
    whisperOptionalPlaceholder: "Escribe aqui lo que sientes",
    spiritLine: "Respira, vuelve a tu centro y deja que tu verdad aparezca.",
    readTarot: "Leer mi tarot",
    wait: "Espera",
    waitMoment: "Espera un momento...",
    loadingPoem: "En el silencio antes de la claridad, las cartas toman forma desde la intuicion.",
    readingTitle: "Tu lectura mistica de tarot",
    share: "Compartir",
    copy: "Copiar",
    copied: "Copiado",
    donationText:
      "Si este espacio te ha dado claridad, consuelo o un momento de paz, puedes dejar una ofrenda para ayudar a mantener esta luz encendida para otras personas.",
    donatePaypal: "Donar con PayPal",
    customAmount: "O escribe tu propia cantidad",
    otherAmount: "Otra cantidad",
    cooldownSelect: "Elige primero una tirada de cartas.",
    cooldownOther: "Describe lo que estas sintiendo para continuar.",
    cooldownWait: "El tarot habla despacio. Deja que la lectura repose antes de preguntar de nuevo.",
    quickFeatures: [
      {
        key: "yesno",
        title: "Tarot si o no",
        to: "/yes-no-tarot",
        srcs: ["/icons/yesno.web", "/icons/yesno.webp", "/icons/yesno.png"],
        explain: "Haz una pregunta concreta y saca una carta para recibir un Si, No o Espera. Simple y directo.",
      },
      {
        key: "readmycard",
        title: "Leer mis cartas",
        to: "/readmycard",
        srcs: ["/icons/readmycard.webp", "/icons/readmycard.png"],
        explain: "Elige tus propias cartas y recibe significados claros para cada una, al derecho o invertidas.",
      },
      {
        key: "tarotscope",
        title: "Tarotscopo",
        to: "/tarotscope",
        srcs: ["/icons/tarotscope.web", "/icons/tartoscope.webp", "/icons/tarotscope.webp", "/icons/tarotscope.png"],
        explain: "Descubre como el tarot se une con la astrologia para darte una guia alineada con tu signo.",
      },
    ],
    emotions: [
      { key: "Uncertain-Love", apiLabel: "amor incierto", label: "Amor incierto", description: "No sabes si tu corazon esta siendo visto y correspondido." },
      { key: "Heart-broken", apiLabel: "corazon roto", label: "Corazon roto", description: "Estas haciendo duelo por un amor que se perdio." },
      { key: "Anxious", apiLabel: "ansiedad", label: "Ansiedad", description: "Te preocupa lo que puede venir." },
      { key: "Confused", apiLabel: "confusion", label: "Confusion", description: "Te sientes entre caminos, decisiones o senales." },
      { key: "Lonely", apiLabel: "soledad", label: "Soledad", description: "Anhelas amor, conexion o comprension." },
      { key: "Hopeless", apiLabel: "desesperanza", label: "Desesperanza", description: "Sientes que nada va a cambiar." },
      { key: "Guilty", apiLabel: "culpa", label: "Culpa", description: "El pasado vuelve con preguntas y peso emocional." },
      { key: "Betrayed", apiLabel: "traicion", label: "Traicion", description: "Te duele una confianza rota o una promesa perdida." },
      { key: "Insecure", apiLabel: "inseguridad", label: "Inseguridad", description: "Dudas de tu valor o de tu lugar en el corazon de alguien." },
      { key: "Overwhelmed", apiLabel: "agobio", label: "Agobio", description: "Sientes demasiadas emociones o expectativas a la vez." },
      { key: "Other", apiLabel: "otra emocion", label: "Otra emocion", description: "Algo mas se mueve dentro de ti." },
    ],
    spreads: [
      { value: 1, title: "El susurro", icon: "Spread1.png", desc: "Una sola carta para reflejar con suavidad tu estado emocional actual." },
      { value: 3, title: "El hilo", icon: "Spread3.png", desc: "Tres cartas que siguen tu camino: de donde vienes, donde estas y hacia donde puedes ir." },
      { value: 5, title: "El espejo", icon: "Spread5.png", desc: "Cinco cartas para mirar mas profundo el terreno emocional y orientar tu proximo paso." },
    ],
  };

  // word-count + excerpt helpers
  const wc = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
  const excerptOf = (s, maxChars = 600) => (s || "").replace(/\s+/g, " ").slice(0, maxChars) + (s && s.length > maxChars ? "..." : "");

  const looksEnglishFateFriday = (text = "") =>
    /\b(Weekly Guidance|The Power of Reflection|This week|opportunity to take a step back|evaluate where you are|self-assessment|moving forward|what is working)\b/i.test(
      String(text)
    );

  const looksEnglishMoonGuide = (text = "") =>
    /\b(Strawberry Moon|The Full|This phase of the moon|encourages you|acknowledge the sweetness|moving forward|take advantage|next few days|moon reaches its peak)\b/i.test(
      String(text)
    );

  const MOON_NAME_ES = {
    "Wolf Moon": "Luna del Lobo",
    "Snow Moon": "Luna de Nieve",
    "Worm Moon": "Luna del Gusano",
    "Pink Moon": "Luna Rosa",
    "Flower Moon": "Luna de las Flores",
    "Strawberry Moon": "Luna de Fresa",
    "Buck Moon": "Luna del Ciervo",
    "Sturgeon Moon": "Luna del Esturion",
    "Harvest Moon": "Luna de la Cosecha",
    "Hunter's Moon": "Luna del Cazador",
    "Hunter\u2019s Moon": "Luna del Cazador",
    "Beaver Moon": "Luna del Castor",
    "Cold Moon": "Luna Fria",
    "Blue Moon": "Luna azul",
  };

  const MOON_MEANING_ES = {
    "A call to courage, community, and honest voice.": "Un llamado a la valentia, la comunidad y una voz honesta.",
    "Patience, rest, and steady inner work.": "Paciencia, descanso y trabajo interior sostenido.",
    "Small beginnings that soften the ground for growth.": "Pequenos comienzos que preparan el terreno para crecer.",
    "Tender renewal, friendship, and fresh perspective.": "Renovacion suave, amistad y una mirada fresca.",
    "Creative bloom, beauty, and shared joy.": "Creatividad en flor, belleza y alegria compartida.",
    "Gratitude, sweetness, and closing gentle loops.": "Gratitud, dulzura y cierres hechos con cuidado.",
    "Strength, confidence, and healthy boundaries.": "Fuerza, confianza y limites sanos.",
    "Depth, wisdom, and sustainable effort.": "Profundidad, sabiduria y esfuerzo sostenible.",
    "Gather results, honor labor, and share the table.": "Recoger resultados, honrar el trabajo y compartir lo aprendido.",
    "Focus, integrity, and practical strategy.": "Enfoque, integridad y estrategia practica.",
    "Preparation, craft, and building safe spaces.": "Preparacion, oficio y construccion de espacios seguros.",
    "Clarity, simplicity, and faithful light in quiet.": "Claridad, sencillez y una luz fiel en el silencio.",
  };

  function translateMoonName(name, isSpanish) {
    if (!isSpanish) return name;
    return MOON_NAME_ES[name] || name;
  }

  function translateMoonMeaning(meaning, isSpanish) {
    if (!isSpanish) return meaning;
    return MOON_MEANING_ES[meaning] || meaning;
  }

  function spanishMoonHomeFallback(hero, copy) {
    const moonName = translateMoonName(hero?.name || copy.fullMoon, true);
    const meaning = translateMoonMeaning(hero?.meaning || "", true);
    return `${moonName} abre un espacio para agradecer, cerrar ciclos suaves y recuperar claridad emocional.${meaning ? ` Su tema es ${meaning.toLowerCase()}` : ""} Mira lo que has sostenido, decide que asuntos necesitan una conversacion honesta y elige un gesto concreto que te ayude a descansar mejor en los proximos dias.`;
  }

  // Friday anchor for weekly caching
  function fridayAnchorUTC(date = new Date(), hourUTC = 9) {
    const now = date;
    const dow = now.getUTCDay(); // 0 Sun ... 5 Fri
    const daysSinceFriday = (7 + dow - 5) % 7;
    const lastFriday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    lastFriday.setUTCDate(lastFriday.getUTCDate() - daysSinceFriday);
    lastFriday.setUTCHours(hourUTC, 0, 0, 0);
    if (now < lastFriday) lastFriday.setUTCDate(lastFriday.getUTCDate() - 7);
    return lastFriday;
  }

  /* ==============================
    Component
  ============================== */
  export default function TarotApp() {
    const lang = currentLanguage();
    const isSpanish = lang === "es";
    const homeCopy = useMemo(
      () =>
        isSpanish
          ? HOME_COPY_ES
          : {
          title: "TarotFate | Emotion-Based Online Tarot Readings",
          description: metaDescription,
          keywords,
          heroLine1: "In this sacred stillness, the heart speaks in symbols and silence",
          heroLine2: "Your emotion leads. The tarot cards reflect it.",
          feelingPrompt: "Select an emotion below to explore a personalized tarot reflection.",
          publishedWeekly: "Published Weekly:",
          loading: "Loading...",
          fateFriday: "Fate Friday",
          fateFridayTitleFallback: "Weekly Guidance: The Power of Reflection",
          fateFridayFallback:
            "This week's guide focuses on choosing one action that steadies your day and clears a small path forward.",
          fateFridayCta: "Read Fate Friday guide",
          fullMoonHighlight: "Full Moon Highlight",
          fullMoon: "Full Moon",
          fullMoonFallback: "A practical moon guide will be available soon.",
          fullMoonCta: "Read the Full Moon guide",
          selectSpread: "Select a card spread first",
          selectSpreadMessage:
            "Select a 'card spread' below. Each tarot card will be drawn in tune with your emotion, offering insight that is uniquely yours.",
          otherRequired: "To proceed with 'Other', please describe what you're feeling.",
          whisperPrompt:
            "For a deeper & more personalized tarot reading, also share your heart's whispers with the universe.",
          whisperRequiredPlaceholder: "Write your emotion (required)",
          whisperOptionalPlaceholder: "Write your whispers here (optional)",
          spiritLine: "Center your spirit and let your truth rise",
          readTarot: "Read My Tarot",
          wait: "Wait",
          waitMoment: "Wait a moment please...",
          loadingPoem: "In the hush before insight, the cards are being born from stardust and silence...",
          readingTitle: "Your Mystic Tarot Card Reading",
          share: "Share",
          copy: "Copy",
          copied: "Copied",
          donationText:
            "If this space has brought you clarity, comfort or even a moment of peace, consider leaving an offering to help keep the light on for others who may wander in.",
          donatePaypal: "Donate via PayPal",
          customAmount: "Or enter your own amount (max $1,000)",
          otherAmount: "Other ($)",
          cooldownSelect: "Select a card spread first",
          cooldownOther: "Please describe what you're feeling to proceed.",
          cooldownWait: "The tarot speaks in whispers, not in haste. Let its magic settle before seeking again.",
          quickFeatures: QUICK_FEATURES,
          emotions,
          spreads,
          },
      [isSpanish]
    );
    const pagePath = isSpanish ? "/es" : "/";
    const localizedPageUrl = `${siteUrl}${pagePath === "/" ? "/" : pagePath}`;
    const localizedFeatures = homeCopy.quickFeatures.map((feature) => ({
      ...feature,
      to: isSpanish ? `/es${feature.to}` : feature.to,
    }));
    const localizedEmotionOptions = homeCopy.emotions.map((emotion) => ({
      key: emotion.key || emotion.label,
      apiLabel: emotion.apiLabel || emotion.label,
      label: emotion.label,
      description: emotion.description,
    }));
    const localizedSpreadOptions = homeCopy.spreads;

    // existing state
    const [selectedEmotion, setSelectedEmotion] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [customWhisper, setCustomWhisper] = useState("");
    const [readingStarted, setReadingStarted] = useState(false);
    const [selectedSpread, setSelectedSpread] = useState(null);
    const [lastReadingTime, setLastReadingTime] = useState(null);
    const [cooldownMessage, setCooldownMessage] = useState("");
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [mockReading, setMockReading] = useState("");
    const [loading, setLoading] = useState(false);
    const [showDonation, setShowDonation] = useState(false);
    const [customAmount, setCustomAmount] = useState("");
    const [showCopiedToast, setShowCopiedToast] = useState(false);
    const [popupStep, setPopupStep] = useState(1);

    // NEW: homepage teasers state
    
    const [ffTitle, setFfTitle] = useState("Fate Friday");
    const [ffNote, setFfNote] = useState("");
    const [ffLoading, setFfLoading] = useState(true);

    const [fmHero, setFmHero] = useState(null); // { name, dateUTC, meaning }
    const [fmNote, setFmNote] = useState("");
    const [fmLoading, setFmLoading] = useState(true);

    const donationAmounts = [5, 10, 20, 50, 100];

    // Step progression
    useEffect(() => {
      if (showPopup && !readingStarted) setPopupStep(selectedSpread ? 2 : 1);
    }, [selectedSpread, showPopup, readingStarted]);

    // Cooldown countdown
    useEffect(() => {
      let id;
      if (cooldownSeconds > 0) {
        id = setInterval(() => setCooldownSeconds((p) => p - 1), 1000);
      } else if (cooldownSeconds === 0 && cooldownMessage) {
        setCooldownMessage("");
      }
      return () => clearInterval(id);
    }, [cooldownSeconds, cooldownMessage]);

    // Scroll lock
    const lockScroll = () => {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    };
    const unlockScroll = () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };

    const openPopup = () => {
      setShowPopup(true);
      setPopupStep(1);
      setSelectedSpread(null);
      lockScroll();
    };

    const reset = () => {
      unlockScroll();
      setSelectedEmotion(null);
      setShowPopup(false);
      setCustomWhisper("");
      setReadingStarted(false);
      setSelectedSpread(null);
      setCooldownMessage("");
      setCooldownSeconds(0);
      setMockReading("");
      setLoading(false);
      setPopupStep(1);
    };

    const goBackToStep1 = () => {
      setPopupStep(1);
      setSelectedSpread(null);
      setReadingStarted(false);
    };

    const handleBackButton = () => {
      if (popupStep === 3) goBackToStep1();
      else if (popupStep === 2) {
        setPopupStep(1);
        setSelectedSpread(null);
      }
    };

    function formatTextWithMarkdown(text) {
      const bold = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return bold.replace(/\*(.+?)\*/g, "<em>$1</em>");
    }

    const looksEnglish = (text = "") =>
      /\b(I understand|you are feeling|Card Drawn|This card|The imagery|heartache|sorrow|healing process|Whisper Wisdom|The Three|The Fool|The Magician|of Cups|of Swords|of Wands|of Pentacles)\b/i.test(
        String(text)
      );

    const fallbackSpanishReading = () => {
      const emotion = selectedEmotionMeta?.label || "esta emoción";
      const whisper = customWhisper.trim();
      const cards = [
        "Three of Swords",
        "Temperance",
        "The Star",
        "Six of Cups",
        "Strength",
      ]
        .slice(0, Number(selectedSpread || 1))
        .map(translateCardName);

      const opening = whisper
        ? `Veo que vienes con ${emotion.toLowerCase()} y con algo muy personal en el corazón: ${whisper}. Esta lectura no intenta borrar lo que sientes. Te acompaña para mirar el dolor con más calma y encontrar un paso que sí puedas sostener.`
        : `Veo que vienes con ${emotion.toLowerCase()}, y eso puede sentirse pesado cuando no tienes una respuesta clara. Esta lectura no intenta apresurarte. Te acompaña para ordenar lo que sientes y encontrar un paso que sí puedas sostener.`;

      const sections = cards.map((card, index) => {
        const messages = [
          `${opening}\n\nCarta extraída: **${card}**\nEsta carta reconoce el peso emocional que estás cargando. Te pide no negar lo que duele, pero tampoco dejar que el dolor decida por ti. Respira, nombra lo que sientes y permite que la verdad aparezca sin exigirte resolverlo todo hoy.`,
          `Carta extraída: **${card}**\nDespués de reconocer la herida, esta carta te invita a recuperar equilibrio. No necesitas una decisión perfecta, necesitas una acción honesta. Elige algo pequeño que cuide tu paz, como descansar, escribir lo que no has dicho o tomar distancia de una conversación que aún está muy cargada.`,
          `Carta extraída: **${card}**\nEsta carta abre una posibilidad más suave. Te recuerda que sanar no significa olvidar, sino volver a confiar en tu propia voz. Lo que hoy parece confuso puede volverse más claro cuando dejas de buscar una respuesta desde la urgencia.`,
          `Carta extraída: **${card}**\nAquí la lectura vuelve hacia tus raíces emocionales. Pregúntate qué parte de ti necesita ternura, no juicio. A veces el siguiente paso no es avanzar rápido, sino dejar de abandonarte mientras esperas claridad.`,
          `Carta extraída: **${card}**\nLa última carta habla de fuerza tranquila. No tienes que endurecerte para protegerte. Puedes poner un límite, pedir espacio o elegir silencio sin perder tu sensibilidad.`,
        ];
        return messages[index];
      });

      return `${sections.join("\n\n")}\n\n**Sabiduría de la lectura**\nLo que sientes merece respeto, pero no tiene que gobernar todo tu día. Vuelve a lo concreto: una respiración, una frase escrita, una conversación pausada o una decisión pequeña. La claridad no siempre llega como certeza inmediata; a veces llega como el primer gesto de cuidado hacia ti.`;
    };

    const selectedEmotionMeta = localizedEmotionOptions.find((e) => e.key === selectedEmotion);
    const isOtherSelected = (selectedEmotion || "").toLowerCase() === "other";
    const canProceed = !!selectedSpread && (!isOtherSelected || customWhisper.trim().length > 0);

    // ==============================
    // Tarot reading popup actions
    // ==============================
    const handleReadTarot = async () => {
      if (!selectedSpread) {
        setCooldownMessage(homeCopy.cooldownSelect);
        setTimeout(() => setCooldownMessage(""), 2000);
        return;
      }
      if (isOtherSelected && !customWhisper.trim()) {
        setCooldownMessage(homeCopy.cooldownOther);
        setTimeout(() => setCooldownMessage(""), 2000);
        return;
      }

      const now = Date.now();
      if (lastReadingTime && now - lastReadingTime < 60000) {
        const secondsLeft = Math.ceil((60000 - (now - lastReadingTime)) / 1000);
        setCooldownSeconds(secondsLeft);
        setCooldownMessage(
          `(${homeCopy.wait} ${secondsLeft}s) ${homeCopy.cooldownWait}`
        );
        return;
      }

      setLastReadingTime(now);
      setReadingStarted(true);
      setPopupStep(3);
      setCooldownMessage("");
      setCooldownSeconds(0);
      setLoading(true);

      try {
          const res = await fetch(`${API_BASE}/api/tarot`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withLanguage({
              emotion: selectedEmotionMeta?.apiLabel || selectedEmotion,
              spread: selectedSpread,
              whisper: customWhisper
            }))
        });
        const data = await res.json();
        if (data.reading) {
          setMockReading(isSpanish && looksEnglish(data.reading) ? fallbackSpanishReading() : data.reading);
        } else {
          setMockReading(isSpanish ? fallbackSpanishReading() : "The mystic veil could not be lifted to feel your emotion at this moment. Please try again shortly.");
        }
      } catch (err) {
        setMockReading(isSpanish ? fallbackSpanishReading() : "The mystic veil could not be lifted to feel your emotion at this moment. Please try again shortly.");
      } finally {
        setLoading(false);
      }
    };

    // ==============================
    // NEW: Homepage teasers (Fate Friday + Full Moon)
    // ==============================
    useEffect(() => {
      let cancel = false;

      // FATE FRIDAY
      (async () => {
        try {
          setFfLoading(true);
          const anchor = fridayAnchorUTC();
          const lang = currentLanguage();
          const ffKey = `tf_ff_long_home_${lang}_${anchor.toISOString().slice(0, 10)}`;
          const cached = localStorage.getItem(ffKey);
          if (cached) {
            const p = JSON.parse(cached);
            const cachedTitle = p.title || "";
            const cachedNote = p.note || "";
            if (isSpanish && (looksEnglishFateFriday(cachedTitle) || looksEnglishFateFriday(cachedNote))) {
              localStorage.removeItem(ffKey);
            } else {
              if (!cancel) {
                setFfTitle(isSpanish ? cachedTitle || homeCopy.fateFridayTitleFallback : cachedTitle || homeCopy.fateFriday);
                setFfNote(cachedNote);
                setFfLoading(false);
              }
              return;
            }
          }
          const r = await fetch(`${API_BASE}/api/fatefriday`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withLanguage({
              zodiac: "",
              theme: "reflection",
              tone: "gentle",
              cardName: "",
              weekAnchorUTC: anchor.toISOString()
            }))
          });
          const d = await r.json();
          if (!r.ok) throw new Error(d?.message || `HTTP ${r.status}`);
          const rawTitle = d?.title || homeCopy.fateFriday;
          const rawNote = d?.note || "";
          const payload = isSpanish
            ? {
                title: looksEnglishFateFriday(rawTitle) ? homeCopy.fateFridayTitleFallback : rawTitle,
                note: looksEnglishFateFriday(rawNote) ? homeCopy.fateFridayFallback : rawNote || homeCopy.fateFridayFallback,
              }
            : { title: rawTitle, note: rawNote };
          if (!cancel) {
            setFfTitle(payload.title);
            setFfNote(payload.note);
          }
          try { localStorage.setItem(ffKey, JSON.stringify(payload)); } catch {}
        } catch {
          if (!cancel) {
            setFfTitle(homeCopy.fateFriday);
            setFfNote(homeCopy.fateFridayFallback);
          }
        } finally {
          if (!cancel) setFfLoading(false);
        }
      })();

      // FULL MOON
      (async () => {
        try {
          setFmLoading(true);
          const y = new Date().getUTCFullYear();
          const lang = currentLanguage();
          const r = await fetch(apiUrlWithLang(`${API_BASE}/api/fullmoons?year=${y}`));
          const d = await r.json();
          if (!r.ok) throw new Error(d?.message || `HTTP ${r.status}`);
          const next = d?.next || d?.previous;
          if (!next) throw new Error("No moon found");
          const localizedNext = isSpanish
            ? {
                ...next,
                name: translateMoonName(next.name, true),
                meaning: translateMoonMeaning(next.meaning, true),
              }
            : next;
          if (!cancel) setFmHero(localizedNext);

          const fmKey = `tf_fm_long_home_${lang}_${localizedNext.dateUTC}`;
          const cached = localStorage.getItem(fmKey);
          if (cached) {
            const p = JSON.parse(cached);
            const cachedNote = p.note || "";
            if (isSpanish && looksEnglishMoonGuide(cachedNote)) {
              localStorage.removeItem(fmKey);
            } else {
              if (!cancel) {
                setFmNote(cachedNote);
                setFmLoading(false);
              }
              return;
            }
          }
          const r2 = await fetch(`${API_BASE}/api/moon-blessing`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withLanguage({
              zodiac: "",
              moonName: localizedNext.name || homeCopy.fullMoon,
              fullMoonDateUTC: localizedNext.dateUTC,
              meaning: localizedNext.meaning || "",
              tone: "gentle"
            }))
          });
          const d2 = await r2.json();
          if (!r2.ok) throw new Error(d2?.message || `HTTP ${r2.status}`);
          const rawNote = d2?.note || "";
          const payload = {
            note: isSpanish && looksEnglishMoonGuide(rawNote)
              ? spanishMoonHomeFallback(localizedNext, homeCopy)
              : rawNote || (isSpanish ? spanishMoonHomeFallback(localizedNext, homeCopy) : homeCopy.fullMoonFallback),
          };
          if (!cancel) setFmNote(payload.note);
          try { localStorage.setItem(fmKey, JSON.stringify(payload)); } catch {}
        } catch {
          if (!cancel) setFmNote(homeCopy.fullMoonFallback);
        } finally {
          if (!cancel) setFmLoading(false);
        }
      })();

      return () => { cancel = true; };
    }, [lang, homeCopy, isSpanish]);

    // ==============================
    // UI helpers
    // ==============================
    const ffPublished = useMemo(() => fridayAnchorUTC(), []);
    const getEmotionImagePath = (label) => `/icons/${label.replace(/\s+/g, "_")}.webp`;
    const fmLocal = useMemo(() => {
      if (!fmHero?.dateUTC) return "";
      try {
        return new Intl.DateTimeFormat(isSpanish ? "es" : undefined, {
          year: "numeric", month: "long", day: "numeric",
          hour: "numeric", minute: "2-digit", timeZoneName: "short",
        }).format(new Date(fmHero.dateUTC));
      } catch {
        return fmHero.dateUTC;
      }
    }, [fmHero, isSpanish]);
    const safeFfTitle = isSpanish && looksEnglishFateFriday(ffTitle)
      ? homeCopy.fateFridayTitleFallback
      : ffTitle || homeCopy.fateFriday;
    const safeFfNote = isSpanish && looksEnglishFateFriday(ffNote)
      ? homeCopy.fateFridayFallback
      : ffNote || homeCopy.fateFridayFallback;
    const safeFmName = translateMoonName(fmHero?.name, isSpanish) || homeCopy.fullMoon;
    const safeFmNote = isSpanish && looksEnglishMoonGuide(fmNote)
      ? spanishMoonHomeFallback(fmHero, homeCopy)
      : fmNote || (isSpanish ? spanishMoonHomeFallback(fmHero, homeCopy) : homeCopy.fullMoonFallback);

    return (
      <div className={`${FONT} text-white min-h-screen`}>
        {/* SEO */}
        <Seo
          title={homeCopy.title}
          description={homeCopy.description}
          url={localizedPageUrl}
          image={ogImage}
          schema={{
            ...schemaGraph,
            "@graph": schemaGraph["@graph"].map((item) => ({
              ...item,
              ...(item["@type"] === "WebSite" || item["@type"] === "WebPage" ? { inLanguage: lang } : {}),
              ...(item["@type"] === "WebPage"
                ? {
                    "@id": `${localizedPageUrl}#webpage`,
                    url: localizedPageUrl,
                    name: homeCopy.title,
                    description: homeCopy.description,
                  }
                : {}),
            })),
          }}
          keywords={homeCopy.keywords}
          alternates={[
            { lang: "x-default", href: siteUrl },
            { lang: "en", href: siteUrl },
            { lang: "es", href: `${siteUrl}/es` },
          ]}
        />

        {/* Prefetch feature pages + preload icons */}
        <Helmet>
          <link rel="prefetch" href="/fate-friday" />
          <link rel="prefetch" href="/full-moon" />
          <link rel="prefetch" href="/read-my-card" />
          <link rel="preload" as="image" href="/icons/readmycard.webp" />
          {localizedEmotionOptions.map((e) => (
            <link key={e.key} rel="preload" as="image" href={`/icons/${e.key.replace(/\s+/g, "_")}.webp`} />
          ))}
          {localizedSpreadOptions.map((s) => (
            <link key={s.icon} rel="preload" as="image" href={`/icons/${s.icon}`} />
          ))}
        </Helmet>

        {/* HERO */}
        <header className={`text-center ${WRAP} ${MAXW} -mt-5 pt-0 pb-2 sm:-mt-3 min-[1180px]:mt-0 min-[1180px]:pt-2`}>
          <p className="mx-auto mb-4 max-w-3xl px-3 text-[18px] font-normal leading-[1.42] text-white/90 sm:mb-5 sm:text-[23px] sm:font-medium sm:text-white">
            {homeCopy.heroLine1}
          </p>

          <Link
            to={isSpanish ? "/es/tarot-cards" : "/tarot-cards"}
            aria-label={isSpanish ? "Ver todas las cartas del tarot" : "Discover Tarot Cards"}
            className="block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9ce7ee]/90 focus-visible:ring-offset-2 focus-visible:ring-offset-[#021021]"
          >
            <Hero />
          </Link>

          <div className="mx-auto mt-3 max-w-4xl px-3 sm:mt-4 sm:px-6">
            <div className="relative py-3 sm:py-4">
              <div aria-hidden className="absolute inset-x-12 bottom-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

              <p className="mx-auto max-w-2xl text-[18px] font-normal leading-[1.42] text-[#f2d38a]/90 sm:text-[23px] sm:font-medium sm:text-[#f2d38a]">
                {homeCopy.heroLine2}
              </p>
            </div>

            <h3 className="mb-4 mt-4 text-[17px] font-medium leading-[1.35] tracking-[0.015em] text-white/90 sm:mb-5 sm:text-[21px] sm:font-semibold sm:tracking-[0.025em] sm:text-white">
              {homeCopy.feelingPrompt}
            </h3>
          </div>
        </header>

        {/* Emotion grid */}
        <section className={`${WRAP} ${MAXW}`}>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10 px-4 max-w-[1200px] mx-auto select-none">
           {localizedEmotionOptions.map((e) => (
<button
  type="button"
  key={e.key}
  onClick={() => {
    setSelectedEmotion(e.key);
    openPopup();
    if (e.key.toLowerCase() === "other") setCustomWhisper("");
  }}
  className="group w-[45%] sm:w-[30%] md:w-[170px] rounded-none cursor-pointer transition-all duration-100 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(88,64,120,0.4)] focus:outline-none focus:ring-2 focus:ring-[#19bed6]/70 text-[#3f2d61] backdrop-blur-md bg-[linear-gradient(180deg,rgba(18,152,181,0.88)_0%,rgba(10,93,137,0.9)_38%,rgba(6,43,77,0.94)_100%)] border border-[#19bed6]/70 select-none shadow-[0_2px_6px_0_rgba(0,0,0,0.8)]"
>
  {/* ICON + GLOW */}
  <div className="flex justify-center pt-4">
    <div className="relative">
      <img
        src={getEmotionImagePath(e.key)}
        alt={`${e.label} emotion icon`}
        width={96}
        height={96}
        fetchpriority="high"
        decoding="async"
        draggable="false"
        className={`${ICON_GLOW_BASE} ${ICON_GLOW_HOV}`}
      />
      {/* white bloom on hover */}
      <span
        aria-hidden
        className="
          absolute inset-0 rounded-lg opacity-0
          group-hover:opacity-100 transition duration-200 ease-out
          blur-[16px]
          bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.55),transparent_70%)]
          pointer-events-none
        "
      />
    </div>
  </div>

  {/* LABELS */}
  <div className="flex flex-col items-center text-center px-3 pb-4 mt-2 select-none">
    <h4 className="text-base font-semibold text-white">
      {e.label}
    </h4>

    {/* ✨ mystic divider */}
    <div className="mt-1 w-11/12 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>

    <p className="pt-3 text-sm italic text-white/85">{e.description}</p>
  </div>
</button>

))}

          </div>
        </section>

{/* Quick Oracles: Yes/No + Tarotscope (Poster-style, sharp edges) */}
<section className={`${WRAP} ${MAXW} mt-8`}>
  <div className="max-w-[1200px] mx-auto px-4">
    <div className="text-center mb-8">
      
      <div role="separator" className="my-2 h-px w-full bg-gradient-to-r from-transparent via-white/70 to-transparent" />
    </div>

    <div className="grid gap-6 sm:grid-cols-3">
      {localizedFeatures.map((f) => (
        <Link
          key={f.key}
          to={f.to}
          className="
            group relative overflow-hidden select-none
            shadow-[0_1px_2px_rgba(0,0,0,0.35)] backdrop-blur-lg
            bg-[linear-gradient(180deg,rgba(18,152,181,0.88)_0%,rgba(10,93,137,0.9)_38%,rgba(6,43,77,0.94)_100%)]
            hover:shadow-[0_1px_4px_rgba(0,0,0,0.45)]
            transition
          "
        >
          {/* thin poster outline */}
          <div className="absolute inset-0 border border-white/25 pointer-events-none" />

          {/* top-down white gradient overlay */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none
                       bg-gradient-to-b from-white/20 via-transparent to-transparent"
          />

          {/* content */}
          <div className="relative z-[1] flex flex-col items-center text-center px-6 py-7 min-h-[320px]">
            {/* icon on top (smaller on mobile) */}
            <ImgFallback
              srcs={f.srcs}
              alt={`${f.title} logo`}
              className="
                w-full h-auto object-contain max-h-[160px] sm:max-h-[180px] md:max-h-[200px]
                drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]
                transition group-hover:brightness-110
              "
            />

            {/* divider line */}
      <div role="separator" className="mt-6 h-px w-full bg-gradient-to-r from-transparent via-white/70 to-transparent" />

            {/* Explanation (italic, readable like your sample) */}
            <p className="mt-3 text-white/85  text-base leading-7">
              {f.explain}
            </p>
          </div>

          {/* gentle hover rim glow */}
          <div className="absolute inset-0 pointer-events-none ring-0 group-hover:ring-1 group-hover:ring-white/80 transition" />
        </Link>
      ))}
    </div>
  </div>
</section>

      

        

        {/* Teasers row: Fate Friday + Full Moon */}
        <main className={`${WRAP} ${MAXW} mt-8 pb-12`}>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Fate Friday teaser */}
            <section className={`${PANEL} p-4 sm:p-6 text-left`}>
              <div className="relative z-[1]">
                <div className="flex items-baseline justify-between gap-3">
  <p className="uppercase tracking-[0.25em] text-sm text-white/85">{homeCopy.fateFriday}</p>
  
</div>
<div
  role="separator"
  className="my-2 h-px w-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
/>
<h2 className="text-lg sm:text-xl font-semibold text-white/95 mt-1">
              {ffLoading ? homeCopy.fateFriday : safeFfTitle}
</h2>
<span className="text-sm sm:text-sm text-white/75">
    {homeCopy.publishedWeekly} {formatFriday(ffPublished, isSpanish ? "es" : undefined)}
  </span>

                <p className={`${TEXT} text-base leading-7 mt-3`}>
                  {ffLoading ? homeCopy.loading : excerptOf(safeFfNote)}
                </p>
                <div className="mt-4">
                  <Link
                    to={isSpanish ? "/es/fate-friday" : "/fate-friday"}
                    className="tf-outline-button"
                  >
                    {homeCopy.fateFridayCta}
                  </Link>
                </div>
              </div>
              <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </section>

            {/* Full Moon teaser */}
            <section className={`${PANEL} p-4 sm:p-6 text-left`}>
              <div className="relative z-[1]">
                <p className="uppercase tracking-[0.25em] text-sm text-white/85">{homeCopy.fullMoonHighlight}</p>
                <div
  role="separator"
  className="my-2 h-px w-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
/>

                <h2 className="text-lg sm:text-xl font-semibold text-white/95 mt-1">
                  {safeFmName}
                </h2>
                <p className="text-sm text-white/85 mt-0.5">
                  {fmHero ? fmLocal : "-"}
                </p>
                <p className={`${TEXT} text-base leading-7 mt-3`}>
                  {fmLoading ? homeCopy.loading : excerptOf(safeFmNote)}
                </p>
                <div className="mt-4">
                  <Link
                    to={isSpanish ? "/es/full-moon" : "/full-moon"}
                    className="tf-outline-button"
                  >
                    {homeCopy.fullMoonCta}
                  </Link>
                </div>
              </div>
              <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </section>
          </div>
        </main>

        {/* POPUP */}
        {showPopup && (
          <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" aria-hidden="true" />
            <div
              role="dialog"
              aria-modal="true"
              className="relative z-10 w-[98%] sm:w-[95%] md:w-[500px] lg:w-[600px] max-h-[80vh] sm:max-h-[82vh] md:max-h-[85vh]
              overflow-y-auto overscroll-contain backdrop-blur-xl 
              bg-gradient-to-b from-[#0B8FBF]/55 via-[#0B67AC]/30 to-[#0B67AE]/10 rounded-xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.25)] border border-white/60 text-[#072021] animate-fadeIn"
            >
              {/* Back button (steps 2-3) */}
              {popupStep !== 1 && !loading && (
                <button
                  onClick={handleBackButton}
                  className="absolute top-4 left-4 bg-white/20 text-[#f3eaff]/80 
                    w-7 h-7 flex items-center justify-center 
                    rounded-full text-xl font-normal 
                    leading-none hover:text-[#0f3d3f]/70 hover:bg-white/60 transition"
                  aria-label="Back"
                >
                  ←
                </button>
              )}
              {/* Close */}
              <button
                onClick={reset}
                className="absolute top-4 right-4 bg-white/20 text-[#f3eaff]/80 
                  w-7 h-7 flex items-center justify-center 
                  rounded-full text-2xl font-normal 
                  leading-none hover:text-[#0f3d3f]/70 hover:bg-white/60 transition"
                aria-label="Close"
              >
                ×
              </button>

              {/* Progress dots */}
              <div className="flex justify-center mb-2 mt-2">
                <div className="flex space-x-2">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className={`w-3 h-3 rounded-full ${popupStep >= step ? "bg-[#2b9cb3]" : "bg-white/30"}`} />
                  ))}
                </div>
              </div>

              {!readingStarted ? (
                <>
                  {/* Heading */}
                  {popupStep === 1 ? (
                    <h3 className="text-xl font-bold text-[#f3eaff] mb-1">{selectedEmotionMeta?.label || selectedEmotion}</h3>
                  ) : (
                    <h3 className="text-xl font-bold text-[#f3eaff] mb-1">
                      {localizedSpreadOptions.find((s) => s.value === selectedSpread)?.title}
                    </h3>
                  )}

                  {/* Divider */}
                  <div className="flex items-center justify-center mb-1">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
                    <span className="px-4 text-[#f3eaff]/70 text-xl drop-shadow-[0_0_6px_rgba(203,183,234,0.8)]">✦</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
                  </div>

                  {/* Step 1: Spread selection */}
                  {popupStep === 1 && (
                    <>
                      <p className="text-base font-normal mb-3 text-[#f3eaff]/80">
                        {homeCopy.selectSpreadMessage}
                      </p>

                      <div className="flex flex-col gap-3 mb-2">
                        {localizedSpreadOptions.map((s) => {
                          const selected = selectedSpread === s.value;
                          return (
                            <button
                              type="button"
                              key={s.value}
                              onClick={() => {
                                setSelectedSpread(s.value);
                                if (!isOtherSelected) setTimeout(() => setPopupStep(2), 300);
                              }}
                              className={`
                                rounded-md cursor-pointer p-3 text-left
                                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(88,64,120,0.4)]
                                focus:outline-none focus:ring-2 focus:ring-[#a991d4]/70 text-[#3f2d61] backdrop-blur-md
                                bg-gradient-to-b from-[#0f3d3f]/5 via-[#14656a]/15 to-[#0f3d3f]/12 border-[1px] border-transparent
                                [background-clip:padding-box,border-box]
                                [border-image:linear-gradient(135deg,#a991d4,#cbb7ea,#cbb7ea,#2b9cb3,#2b9cb3)_1]
                                select-none shadow-[0_4px_20px_0_rgba(0,0,0,0.8)]
                                ${selected ? "ring-2 ring-white scale-[1.02] bg-gradient-to-b from-[#2b9cb3]/20 via-[#2b9cb3]/30 to-[#2b9cb3]/20" : ""}
                              `}
                            >
                              <div className="flex items-center gap-4">
                                <div className="text-base font-medium text-[#f3eaff]">{s.title}</div>
                                <img
                                  src={`/icons/${s.icon}`}
                                  alt={`${s.value} card spread icon`}
                                  className="h-6 object-contain drop-shadow-[0_1px_1px_rgba(169,145,212,0.3)]"
                                  draggable="false"
                                />
                              </div>
                              <p className="mt-2 text-base italic text-white/80">{s.desc}</p>
                            </button>
                          );
                        })}
                      </div>

                      {/* Cooldown */}
                      {cooldownMessage && (
                        <div className="mt-4 p-3 bg-[#0f3d3f]/40 rounded-lg border border-[#2b9cb3]/50">
                          <p className="text-sm text-[#f3eaff]">
                            {cooldownMessage}
                            {cooldownSeconds > 0 && (
                              <span className="block mt-1 text-[#cbb7ea] font-medium">{homeCopy.wait} {cooldownSeconds}s</span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Ads placeholder */}
                    
                    </>
                  )}

                  {/* Step 2: Whisper */}
                  {popupStep === 2 && (
                    <>
                      <p className="text-base font-normal mb-2 text-[#f3eaff] text-center">
                        {isOtherSelected
                          ? homeCopy.otherRequired
                          : homeCopy.whisperPrompt}
                      </p>

                      <textarea
                        value={customWhisper}
                        onChange={(e) => setCustomWhisper(e.target.value)}
                        className="w-full border border-[#0f3d3f] bg-white rounded p-3 text-base text-gray-1000 placeholder-[#6f7373] mb-3"
                        rows={3}
                        placeholder={isOtherSelected ? homeCopy.whisperRequiredPlaceholder : homeCopy.whisperOptionalPlaceholder}
                      />

                      <p className="text-sm text-[#fff] italic mb-4 text-center">{homeCopy.spiritLine}</p>

                      {cooldownMessage && (
                        <div className="mb-4 p-3 bg-[#0f3d3f]/40 rounded-lg border border-[#2b9cb3]/50">
                          <p className="text-sm text-[#f3eaff]">
                            {cooldownMessage}
                            {cooldownSeconds > 0 && (
                              <span className="block mt-1 text-[#cbb7ea] font-medium">{homeCopy.wait} {cooldownSeconds}s</span>
                            )}
                          </p>
                        </div>
                      )}

                      <button
                        onClick={handleReadTarot}
                        className={`w-full px-6 py-3 rounded-xl text-lg font-bold transition-all duration-100 border-2
                          ${
                            canProceed && cooldownSeconds === 0
                              ? "bg-gradient-to-r from-[#2b9cb3] to-[#2b9cb3] border-white/50 text-white shadow-xl hover:shadow-lg hover:from-[#14656a] hover:via-[#2b9cb3] hover:to-[#14656a]"
                              : "bg-gray-200 text-gray-400 border-[#2b9cb3] cursor-not-allowed"
                          }`}
                        disabled={!canProceed || cooldownSeconds > 0}
                      >
                        {cooldownSeconds > 0 ? `${homeCopy.wait} ${cooldownSeconds}s` : homeCopy.readTarot}
                      </button>

                      {/* Ads placeholder */}
                     
                    </>
                  )}
                </>
              ) : loading ? (
                <div className="flex flex-col items-center justify-center py-10 animate-pulse">
                  <div className="text-lg font-bold text-[#f3eaff] text-center max-w-sm">{homeCopy.waitMoment}</div>
                  <div className="text-lg text-[#f3eaff] italic text-center max-w-sm">
                    <br />"{homeCopy.loadingPoem}"
                  </div>
                </div>
              ) : (
                <>
                  <h4 className="text-lg font-semibold text-[#f3eaff] mb-3">{homeCopy.readingTitle}</h4>
                  <div className="bg-white/90 bg-opacity-95 border border-[#cbb7ea] rounded-lg shadow-md p-6 text-left text-[#0A2829] text-[17px] leading-relaxed space-y-1">
                    {mockReading.split("\n\n").map((para, index, arr) => (
                      <div key={index}>
                        <p
                          dangerouslySetInnerHTML={{ __html: formatTextWithMarkdown(para) }}
                          className="whitespace-pre-wrap"
                        />
                        {index < arr.length - 1 && <div className="flex justify-center my-1 text-[#cbb7ea] text-xl"></div>}
                      </div>
                    ))}
                  </div>

                  {/* Share + Copy */}
                  <div className="flex justify-between items-center mt-4 gap-2">
                    <button
                      onClick={async () => {
                        if (navigator.share) {
                          await navigator.share({ title: homeCopy.readingTitle, text: mockReading });
                        } else {
                          alert(isSpanish ? "Tu dispositivo no permite compartir desde aqui." : "Sharing is not supported on your device.");
                        }
                      }}
                      className="text-sm text-white bg-[#14656a] hover:bg-[#2b9cb3]/70 border border-white hover:border-white px-4 py-1.5 rounded-full transition"
                    >
                      {homeCopy.share}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(mockReading);
                        setShowCopiedToast(true);
                        setTimeout(() => setShowCopiedToast(false), 2000);
                      }}
                      className="text-sm text-white bg-[#14656a] hover:bg-[#2b9cb3]/70 border border-white hover:border-white px-4 py-1.5 rounded-full transition"
                    >
                      {homeCopy.copy}
                    </button>
                  </div>
                  <div className="flex flex-col items-end mt-1">
                    {showCopiedToast && <div className="mt-1 text-xs text-white/80 bg-transparent px-2 py-1">{homeCopy.copied}</div>}
                  </div>

                  {/* Donation */}
                  <div className="mt-6 bg-white/90 p-4 rounded-lg border border-[#decbaa]">
                    <p className="text-sm font-normal text-[#0f3d3f]">
                      {homeCopy.donationText}
                    </p>
                    <div className="mt-4 flex flex-col items-center">
                      <button
                        onClick={() => setShowDonation(!showDonation)}
                        className="bg-[#14656a] text-white px-5 py-2 rounded-full shadow hover:bg-[#2b9cb3] hover:border-[#14656a] transition"
                      >
                        {homeCopy.donatePaypal}
                      </button>
                      {showDonation && (
                        <div className="mt-4 w-full flex flex-col items-center space-y-3">
                          <div className="flex flex-wrap justify-center gap-3">
                            {donationAmounts.map((amount) => (
                              <a
                                key={amount}
                                href={`https://www.paypal.me/tarotfatecom/${amount}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white border border-[#14656a] text-[#14656a] px-4 py-2 rounded-xl text-sm hover:bg-[#14656a] hover:text-white transition"
                              >
                                ${amount}
                              </a>
                            ))}
                          </div>
                          <div className="text-center mt-3">
                            <p className="text-sm text-[#14656a] mb-1">{homeCopy.customAmount}</p>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={customAmount}
                              onChange={(e) => {
                                const value = e.target.value.trim();
                                const isValid = /^(\d+(\.\d{0,2})?)?$/.test(value);
                                if (isValid && parseFloat(value || "0") <= 1000) setCustomAmount(value);
                              }}
                              className="border border-[#14656a] rounded px-3 py-1 w-32 text-center text-[#14656a]"
                              placeholder={homeCopy.otherAmount}
                            />
                            {customAmount && parseFloat(customAmount) > 0 && (
                              <div className="mt-2">
                                <a
                                  href={`https://www.paypal.me/tarotfatecom/${customAmount}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block bg-[#14656a] text-white px-4 py-1.5 rounded-full shadow-lg hover:bg-[#2b9cb3] transition text-sm"
                                >
                                  {homeCopy.donatePaypal} ${customAmount}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
